import React, { useEffect, useRef, useState } from "react";
import { Animated, Easing, View, StyleSheet } from "react-native";
import Svg, { Circle, Path } from "react-native-svg";
import CCText from "@/Atoms/CCText";

// A chess-clock styled countdown to an invite's expiry. Reads like the analog
// dual-button clock on a chess table: a domed body, a ticking dial, and the
// remaining time in tabular digits. Calm aqua while there's plenty of time,
// warming to amber then red in the final stretch so urgency reads at a glance.

type Tone = {
  body: string;   // clock body
  face: string;   // dial face
  ring: string;   // dial ring / accent
  ink: string;    // digits
  hand: string;   // clock hands
};
const CALM: Tone = { body: "#2C7F76", face: "#F5EFE0", ring: "#3DAB9E", ink: "#F5EFE0", hand: "#2C2523" };
const WARN: Tone = { body: "#B27F0E", face: "#FEF2B5", ring: "#E6A817", ink: "#4D3F37", hand: "#4D3F37" };
const CRIT: Tone = { body: "#9E1B2F", face: "#FFE1E1", ring: "#CD2C39", ink: "#fff", hand: "#7A1120" };

function toneFor(ms: number): Tone {
  if (ms <= 15 * 60 * 1000) return CRIT;   // < 15m
  if (ms <= 60 * 60 * 1000) return WARN;   // < 1h
  return CALM;
}

// Small analog chess-clock dial; the second hand sweeps once per minute.
function Dial({ seconds, tone, size = 26 }: { seconds: number; tone: Tone; size?: number }) {
  const angle = (seconds % 60) * 6 - 90; // degrees, 12 o'clock = -90
  const r = size / 2;
  const handLen = r * 0.62;
  const x2 = r + handLen * Math.cos((angle * Math.PI) / 180);
  const y2 = r + handLen * Math.sin((angle * Math.PI) / 180);
  return (
    <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <Circle cx={r} cy={r} r={r - 1} fill={tone.face} stroke={tone.ring} strokeWidth={2} />
      {/* 12 / 3 / 6 / 9 ticks */}
      {[0, 90, 180, 270].map((a) => {
        const rad = ((a - 90) * Math.PI) / 180;
        const ix = r + (r - 4) * Math.cos(rad);
        const iy = r + (r - 4) * Math.sin(rad);
        const ox = r + (r - 2) * Math.cos(rad);
        const oy = r + (r - 2) * Math.sin(rad);
        return <Path key={a} d={`M${ix} ${iy} L${ox} ${oy}`} stroke={tone.hand} strokeWidth={1.4} strokeLinecap="round" />;
      })}
      <Path d={`M${r} ${r} L${x2} ${y2}`} stroke={tone.hand} strokeWidth={1.6} strokeLinecap="round" />
      <Circle cx={r} cy={r} r={1.8} fill={tone.hand} />
    </Svg>
  );
}

export default function InviteCountdown({
  remaining,
  seconds,
  label,
}: {
  remaining: number; // ms left
  seconds: number;    // current second (0-59) for the sweeping hand
  label: string;      // "2h 05m 00s"
}) {
  const tone = toneFor(remaining);
  const critical = remaining <= 15 * 60 * 1000;

  // Gentle pulse only in the critical window, respecting reduced motion.
  const pulse = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!critical) { pulse.setValue(0); return; }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 700, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 0, duration: 700, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [critical, pulse]);
  const scale = pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.04] });

  return (
    <Animated.View style={[styles.clock, { backgroundColor: tone.body, transform: [{ scale }] }]}>
      <View style={[styles.dialWell, { backgroundColor: tone.ring }]}>
        <Dial seconds={seconds} tone={tone} />
      </View>
      <View style={styles.readout}>
        <CCText style={[styles.eyebrow, { color: tone.ink, opacity: 0.75 }]}>
          {critical ? "Expiring soon" : "Expires in"}
        </CCText>
        <CCText style={[styles.time, { color: tone.ink }]}>{label}</CCText>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  clock: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    alignSelf: "center",
    paddingVertical: 9,
    paddingHorizontal: 13,
    borderRadius: 14,
    marginTop: 14,
    // domed chess-clock body
    shadowColor: "#4D3F37",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.22,
    shadowRadius: 6,
    elevation: 3,
  },
  dialWell: {
    padding: 3,
    borderRadius: 999,
  },
  readout: { alignItems: "flex-start" },
  eyebrow: {
    fontSize: 9.5,
    fontWeight: "700",
    letterSpacing: 0.08,
    textTransform: "uppercase",
  },
  time: {
    fontSize: 17,
    fontWeight: "700",
    fontVariant: ["tabular-nums"],
    letterSpacing: 0.5,
    lineHeight: 20,
  },
});
